import { NgIf } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { firstValueFrom } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, FeatureType, Grid, JobHazardAnalysis, ListItem, ModalSize, resolveCanonicalListValue, VpJob, VpUser, VpVendor } from '../../shared'

interface VendorUserLookup {
    vendorId: number
    displayName: string
}
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { JobHazardAnalysisModalComponent } from './job-hazard-analysis-modal.component'

@Component({
    selector: 'app-job-hazard-analysis-list',
    templateUrl: './job-hazard-analysis-list.component.html',
    standalone: true,
    imports: [
        FormsModule,
        NgIf,
        FaIconComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class JobHazardAnalysisListComponent extends BaseComponent {
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    private destroyRef = inject(DestroyRef)

    /** Set briefly so the modal can scroll to a checklist row (e.g. deep link from home / email). */
    focusJhaItemId: string | null = null

    /** Open JHA modal in correction-focus mode (non-compliant rows only), e.g. `jhaCorrectionFocus=1` on the route. */
    openJhaCorrectionFocus = false

    private pendingOpenJhaId: string | null = null
    private pendingOpenJhaItemId: string | null = null
    private pendingCorrectionFocus = false

    faXmark = faXmark
    grid: Grid<JobHazardAnalysis>
    allJobs: VpJob[] = []
    activeUsers: VpUser[] = []
    vendors: VpVendor[] = []
    vendorUsers: VendorUserLookup[] = []
    jobMap: ListItem<string>[] = []
    userMap: ListItem<number>[] = []
    /** Employees (`e:{hrRef}`) and vendor accounts (`v:{vendorId}`), sorted by name. */
    inspectorMap: ListItem<string>[] = []
    vendorNames: string[] = []

    constructor() {
        super()
        this.grid = new Grid<JobHazardAnalysis>(this.services)
            .setupModalEditing(
                this.services.data.jobHazardAnalyses,
                'Job Hazard Analysis',
                JobHazardAnalysisModalComponent,
                () => JobHazardAnalysis.createNew(),
                this,
                ModalSize.extraLarge,
            )
            .filterBy(item => [item.jobName, item.inspectorName, item.jobSiteLocation, item.status])
            .wrapColumnHeaders()

        // The image URLs and computed names come from the server, so re-pull after add/edit.
        this.grid.modified.on(this.refreshItems.bind(this))

        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.captureJhaOpenIntentFromRoute()
            if (this.pendingOpenJhaId && this.grid.items?.length) {
                queueMicrotask(() => this.tryOpenJhaFromPendingIntent())
            }
        })

        this.refresh()
    }

    refresh() {
        this.captureJhaOpenIntentFromRoute()

        this.loadData().subscribe({
            next: result => this.applyLoadedData(result),
        })
    }

    private loadData() {
        return this.services.data.load(
            true,
            true,
            this.services.data.vpJobs.queryAll(),
            this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
            this.services.data.vendors.queryAll(),
            this.services.data.get<VendorUserLookup[]>('api/User/GetActiveVendorUsers'),
            this.services.data.jobHazardAnalyses.queryAll(),
        )
    }

    private applyLoadedData(result: unknown[]) {
        this.allJobs = result[0] as VpJob[]
        this.activeUsers = result[1] as VpUser[]
        this.vendors = result[2] as VpVendor[]
        this.vendorUsers = result[3] as VendorUserLookup[]
        const items = result[4] as JobHazardAnalysis[]
        this.jobMap = this.allJobs
            .filter(j => j.jobStatus === 1)
            .orderByDescending((x: any) => x.id)
            .map(j => new ListItem(j.id, j.id + ' ' + j.name))
        this.userMap = this.activeUsers.map(u => new ListItem(u.id, u.name))
        this.inspectorMap = [
            ...this.activeUsers.map(u => new ListItem(`e:${u.id}`, u.name ?? '')),
            ...this.vendorUsers.map(v => new ListItem(`v:${v.vendorId}`, v.displayName ?? '')),
        ].sort((a, b) => a.display.localeCompare(b.display))
        this.vendorNames = this.vendors.map(v => v.name).sort()
        this.assignInspectorNames(items)
        this.grid.items = items
        this.initialLoadingComplete()
        this.tryOpenJhaFromPendingIntent()
    }

    private ensureJobMapLoaded(): Promise<void> {
        if (this.jobMap.length > 0) {
            return Promise.resolve()
        }
        return firstValueFrom(this.loadData()).then(result => {
            this.applyLoadedData(result)
        })
    }

    openAddForJob(jobId: string, jobName: string): Promise<any> | null {
        if (!this.canEdit) {
            return null
        }
        return this.ensureJobMapLoaded().then(() => {
            const jha = JobHazardAnalysis.createNew()
            jha.job = resolveCanonicalListValue(jobId, this.jobMap.map(j => j.value)) ?? jobId
            jha.jobName = jobName
            return this.grid.addItemWith(jha)
        })
    }

    private captureJhaOpenIntentFromRoute() {
        const jhaId = this.route.snapshot.queryParamMap.get('openJhaId')
        if (!jhaId) return
        this.pendingOpenJhaId = jhaId
        this.pendingOpenJhaItemId = this.route.snapshot.queryParamMap.get('openJhaItemId')
        const cf = this.route.snapshot.queryParamMap.get('jhaCorrectionFocus')
        this.pendingCorrectionFocus = cf === '1' || cf === 'true'
    }

    private tryOpenJhaFromPendingIntent() {
        const jhaId = this.pendingOpenJhaId
        if (!jhaId) return

        const itemId = this.pendingOpenJhaItemId
        this.pendingOpenJhaId = null
        this.pendingOpenJhaItemId = null

        const clearParams = () => {
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { openJhaId: null, openJhaItemId: null, jhaCorrectionFocus: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            })
        }

        const row = this.grid.items.find(j => String(j.id).toLowerCase() === String(jhaId).toLowerCase())
        if (!row) {
            this.services.notification.warning('Job Hazard Analysis not found')
            clearParams()
            return
        }

        if (itemId) this.focusJhaItemId = String(itemId).toLowerCase()
        this.openJhaCorrectionFocus = this.pendingCorrectionFocus
        this.pendingCorrectionFocus = false
        this.grid.selectedItem = row
        this.grid.editItem(row)
        this.focusJhaItemId = null
        this.openJhaCorrectionFocus = false
        clearParams()
    }

    private refreshItems() {
        this.services.data.jobHazardAnalyses.queryAll().subscribe({
            next: items => {
                this.assignInspectorNames(items)
                this.grid.items = items
            },
        })
    }

    /**
     * The model can't resolve inspector names on its own; the list is the one place we have
     * both employee and vendor lookups, so populate display names here before the grid renders.
     */
    private assignInspectorNames(items: JobHazardAnalysis[]) {
        const byHr = new Map<number, string>(this.userMap.map(u => [u.value, u.display]))
        const byVendor = new Map<number, string>(this.vendorUsers.map(v => [v.vendorId, v.displayName]))
        for (const i of items) {
            if (i.inspectorVendorId != null && i.inspectorVendorId > 0) {
                i.inspectorName = byVendor.get(i.inspectorVendorId) ?? ''
            } else if (i.inspectorHrRef != null) {
                i.inspectorName = byHr.get(i.inspectorHrRef) ?? ''
            }
        }
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisEdit])
    }

    deleteSelectedItem() {
        const selected = this.grid.selectedItem
        if (!selected) return
        this.services.modals.confirm('Delete', 'Are you sure you want to delete this Job Hazard Analysis?').then(() => {
            this.services.data.jobHazardAnalyses.delete(selected).subscribe({
                next: () => this.refreshItems(),
            })
        })
    }
}
